import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Facebook, Instagram, Upload, Edit2, MapPin, 
  Building, Globe
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import BaseEditProfile, { EditSection, EditProfileImage } from '../BaseEditProfile';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import PropertyManager from './PropertyManager';

// Constantes para URLs de Cloudflare
const CLOUDFLARE_PROFILES_URL = 'https://pub-2a10bd66114a4441b95e9249adee01cf.r2.dev';
const CLOUDFLARE_BANNERS_URL = 'https://pub-cdaec3d9feef44cf8950eee9a93266e4.r2.dev';

// Lista de servicios inmobiliarios
const SERVICIOS_INMOBILIARIOS = [
  'Compra y venta de propiedades',
  'Alquiler de inmuebles',
  'Tasaciones profesionales',
  'Asesoría legal inmobiliaria',
  'Gestión de propiedades',
  'Marketing inmobiliario',
  'Inversiones inmobiliarias',
  'Financiamiento inmobiliario',
  'Administración de propiedades',
  'Servicios postventa'
];

// Función mejorada para procesar imágenes de perfil
const getProfileImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // Caso especial: cuando la URL contiene tanto localhost como la URL de Cloudflare
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/profiles/https://pub-') && imageUrl.includes('r2.dev')) {
    // Extraer solo la parte de Cloudflare
    return imageUrl.substring(imageUrl.indexOf('https://pub-'));
  }
  
  // Si ya es una URL de Cloudflare, usarla directamente
  if (imageUrl.includes('r2.dev')) {
    return imageUrl;
  }
  
  // Si es una URL local completa del servidor
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/profiles/')) {
    const filename = imageUrl.split('/profiles/').pop();
    return `${CLOUDFLARE_PROFILES_URL}/${filename}`;
  }
  
  // Si es una ruta relativa con /profiles/
  if (imageUrl.includes('/profiles/')) {
    const filename = imageUrl.split('/profiles/').pop();
    return `${CLOUDFLARE_PROFILES_URL}/${filename}`;
  }
  
  // Si es solo el nombre del archivo
  return `${CLOUDFLARE_PROFILES_URL}/${imageUrl}`;
};

// Función mejorada para procesar banners
const getBannerImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // Caso especial: cuando la URL contiene tanto localhost como la URL de Cloudflare
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/banners/https://pub-') && imageUrl.includes('r2.dev')) {
    // Extraer solo la parte de Cloudflare
    return imageUrl.substring(imageUrl.indexOf('https://pub-'));
  }
  
  // Si ya es una URL de Cloudflare, usarla directamente
  if (imageUrl.includes('r2.dev')) {
    return imageUrl;
  }
  
  // Si es una URL local completa del servidor
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/banners/')) {
    const filename = imageUrl.split('/banners/').pop();
    return `${CLOUDFLARE_BANNERS_URL}/${filename}`;
  }
  
  // Si es una ruta relativa con /banners/
  if (imageUrl.includes('/banners/')) {
    const filename = imageUrl.split('/banners/').pop();
    return `${CLOUDFLARE_BANNERS_URL}/${filename}`;
  }
  
  // Si es solo el nombre del archivo
  return `${CLOUDFLARE_BANNERS_URL}/${imageUrl}`;
};

const EditInmProfile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Estados para imágenes
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  // Estados del perfil
  const [profileData, setProfileData] = useState({
    nombre: '',
    nombre_empresa: '',
    ruc: '',
    slogan: '',
    experiencia: '',
    anos_experiencia: '',
    sobre_nosotros: '',
    facebook: '',
    instagram: '',
    ubicacion: '',
    direccion_sede: '',
    sitio_web: '',
    servicios: []
  });

  // Estado para propiedades destacadas
  const [featuredPropertyIds, setFeaturedPropertyIds] = useState([]);

  // Manejadores de cambios
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    setHasUnsavedChanges(true);
  };

  const handleServiceChange = (service) => {
    setProfileData(prev => {
      const newServices = prev.servicios.includes(service)
        ? prev.servicios.filter(s => s !== service)
        : [...prev.servicios, service];
      
      // Limitar a 6 servicios
      const finalServices = newServices.slice(0, 6);
      
      return { 
        ...prev, 
        servicios: finalServices 
      };
    });
    setHasUnsavedChanges(true);
  };

  const handleLocationSelect = (location) => {
    setProfileData(prev => ({
      ...prev,
      ubicacion: `${location.city}, ${location.province}, ${location.country}`,
      ciudad: location.city,
      provincia: location.province,
      pais: location.country
    }));
    setHasUnsavedChanges(true);
  };

  const handleFeaturedPropertiesUpdate = (selectedIds) => {
    setFeaturedPropertyIds(selectedIds);
    setHasUnsavedChanges(true);
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
      setHasUnsavedChanges(true);
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      setProfileImagePreview(URL.createObjectURL(file));
      setHasUnsavedChanges(true);
    }
  };

  // Cargar datos del perfil
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          throw new Error('No se encontró token de autenticación');
        }
  
        const response = await fetch('https://backend-production-1786.up.railway.app/api/inmobiliarias/perfil-detallado', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
  
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error al cargar el perfil: ${response.status} ${errorText}`);
        }
  
        const data = await response.json();
        
        // Actualizar estado con los datos recibidos
        setProfileData({
          nombre: data.nombre || '',
          nombre_empresa: data.nombre_empresa || '',
          ruc: data.ruc || '',
          slogan: data.slogan || '',
          sobre_nosotros: data.sobre_nosotros || '',
          sitio_web: data.slug || '',
          experiencia: data.experiencia || '',
          anos_experiencia: data.anos_experiencia || '',
          direccion_sede: data.direccion_sede || '',
          ubicacion: data.ubicacion || '',
          facebook: data.facebook || '',
          instagram: data.instagram || '',
          whatsapp: data.whatsapp || '',
          servicios: data.servicios || []
        });
  
        // Procesar URLs de imágenes correctamente usando las funciones mejoradas
        if (data.foto_perfil) {
          const profileUrl = getProfileImageUrl(data.foto_perfil);
          console.log('URL de imagen de perfil procesada:', profileUrl);
          setProfileImagePreview(profileUrl);
        }
  
        if (data.banner_image) {
          const bannerUrl = getBannerImageUrl(data.banner_image);
          console.log('URL de banner procesada:', bannerUrl);
          setBannerPreview(bannerUrl);
        }
  
      } catch (error) {
        console.error('Error al cargar datos del perfil:', error);
        setError(`No se pudieron cargar los datos del perfil: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
  
    fetchProfileData();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      // Añadir archivos si se han cambiado
      if (bannerFile) formData.append('banner', bannerFile);
      if (profileImageFile) formData.append('profile_image', profileImageFile);
  
      // Añadir el resto de datos
      Object.keys(profileData).forEach(key => {
        if (key === 'servicios') {
          formData.append(key, JSON.stringify(profileData[key]));
        } else {
          formData.append(key, profileData[key]);
        }
      });
  
      // Actualizar perfil
      const response = await fetch('https://backend-production-1786.up.railway.app/api/vendedores/perfil', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar los cambios');
      }

      // Actualizar propiedades destacadas
      if (featuredPropertyIds.length > 0) {
        await fetch('https://backend-production-1786.up.railway.app/api/vendedores/featured-properties', {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ propertyIds: featuredPropertyIds })
        });
      }
  
      setHasUnsavedChanges(false);
      navigate('/mi_perfil');
    } catch (error) {
      setError(error.message);
      console.error('Error al guardar:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseEditProfile
      title="Información de la Inmobiliaria"
      loading={loading}
      error={error}
      bannerPreview={bannerPreview}
      onBannerChange={handleBannerChange}
      onSave={handleSave}
      onCancel={() => navigate('/mi_perfil')}
    >
      {/* Información de la Empresa */}
      <EditSection title="Información de la Empresa">
        <div className="flex items-start gap-8">
          <EditProfileImage
            src={profileImagePreview}
            onChange={handleProfileImageChange}
          />
          
          <div className="flex-1 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la Inmobiliaria
                </label>
                <input
                  type="text"
                  name="nombre_empresa"
                  value={profileData.nombre_empresa || ''}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nombre de la inmobiliaria"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RUC
                </label>
                <input
                  type="text"
                  name="ruc"
                  value={profileData.ruc || ''}
                  onChange={handleInputChange}
                  maxLength="11"
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="RUC"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Slogan
              </label>
              <input
                type="text"
                name="slogan"
                value={profileData.slogan || ''}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Slogan de la inmobiliaria"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sitio Web
              </label>
              <input
                type="url"
                name="sitio_web"
                value={profileData.sitio_web || ''}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://www.ejemplo.com"
              />
            </div>
          </div>
        </div>
      </EditSection>

      {/* About Section */}
      <EditSection title="Sobre Nosotros">
        <textarea
          name="sobre_nosotros"
          value={profileData.sobre_nosotros || ''}
          onChange={handleInputChange}
          placeholder="Cuéntanos más sobre ti..."
          className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[150px]"
        />
        <p className="text-sm text-gray-500 mt-2">
          Esta información aparecerá en tu perfil público.
        </p>
      </EditSection>

      {/* Ubicación */}
      <EditSection title="Ubicación">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ubicación Principal
            </label>
            <LocationAutocomplete 
              onLocationSelect={handleLocationSelect}
              initialValue={profileData.ubicacion}
            />
          </div>
        </div>
      </EditSection>

      {/* Servicios Ofrecidos */}
      <EditSection title="Servicios Inmobiliarios">
        <div className="space-y-6">
          <p className="text-sm text-gray-500">
            Selecciona hasta 6 servicios principales
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SERVICIOS_INMOBILIARIOS.map((servicio) => (
            <label
              key={servicio}
              className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors
                ${profileData.servicios.includes(servicio) 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:bg-gray-50'}`}
            >
              <input
                type="checkbox"
                checked={profileData.servicios.includes(servicio)}
                onChange={() => handleServiceChange(servicio)}
                className="hidden"
              />
              <div 
                className={`w-5 h-5 mr-2 border rounded ${
                  profileData.servicios.includes(servicio) 
                    ? 'bg-blue-500 border-blue-500' 
                    : 'border-gray-400'
                }`}
              >
                {profileData.servicios.includes(servicio) && (
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="white" 
                    strokeWidth="3"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                )}
              </div>
              <span className="text-sm">{servicio}</span>
            </label>
          ))}
          </div>
          {profileData.servicios.length > 6 && (
            <p className="text-red-500 text-sm">
              No puedes seleccionar más de 6 servicios
            </p>
          )}
        </div>
      </EditSection>

      {/* Propiedades Destacadas */}
      <PropertyManager 
        onPropertyUpdate={handleFeaturedPropertiesUpdate}
      />

      {/* Redes Sociales */}
      <EditSection title="Redes Sociales">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center gap-4">
            <Facebook className="w-6 h-6 text-blue-500" />
            <input
              type="text"
              name="facebook"
              value={profileData.facebook}
              onChange={handleInputChange}
              placeholder="Usuario de Facebook"
              className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-4">
            <Instagram className="w-6 h-6 text-pink-500" />
            <input
              type="text"
              name="instagram"
              value={profileData.instagram}
              onChange={handleInputChange}
              placeholder="Usuario de Instagram"
              className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </EditSection>
    </BaseEditProfile>
  );
};

export default EditInmProfile;